import React, {Dispatch, ReactNode, useContext, useReducer} from 'react';
import {DateRange} from '~/lib/date';
import {fetchFeatures, getPerformance, getPerformanceDimensions} from '~/modules/Dashboard/api';
import {IFeature} from '~/modules/Dashboard/models/Feature';
import {
    Granularity,
    IPerformance,
    IPerformanceDimension,
    PerformanceType
} from '~/modules/Dashboard/models/Performance';

export interface IPerformanceQuery {
    performanceType?: PerformanceType;
    from?: Date;
    to?: Date;
    dimensions?: string[];
    granularity?: Granularity;
    chartType?: ChartType;
    sellerId?: number;
    agencyId?: number;
}

export interface IPerformanceReducerState {
    data: IPerformance[];
    dimensions: IPerformanceDimension[];
    features: IFeature[];
    query: IPerformanceQuery;
    isDataPending: boolean;
    isDimensionsPending: boolean;
    isFeaturePending: boolean;
    error: Error;
    table?: {
        short?: boolean;
    };
}

export interface IPerformanceTableState {
    short: boolean;
}

const initialPerformanceTableState: IPerformanceTableState = {
    short: false
};

export enum ChartType {
    TOTAL,
    DIMENSION
}

export type IPerformanceReducerAction =
    | { type: 'data/fetch' }
    | { type: 'data/fetch/success'; data: IPerformance[] }
    | { type: 'data/fetch/failed'; error: Error }
    | { type: 'dimensions/fetch' }
    | { type: 'dimensions/fetch/success'; dimensions: IPerformanceDimension[] }
    | { type: 'dimensions/fetch/failed'; error: Error }
    | { type: 'query/performanceType'; performanceType: PerformanceType }
    | { type: 'query/dateRange'; dateRange: DateRange }
    | { type: 'query/chartType'; chartType: ChartType }
    | { type: 'query/granularity'; granularity: Granularity }
    | { type: 'query/dimensions'; dimensions: string[] }
    | { type: 'query/agencyId'; agencyId: number }
    | { type: 'table/state'; table: IPerformanceTableState }
    | { type: 'features/fetch' }
    | { type: 'features/fetch/success' }
    | { type: 'features/fetch/failed'; error: Error };

const initialPerformanceState: IPerformanceReducerState = {
    data: [],
    dimensions: [],
    features: [],
    query: null,
    isDataPending: false,
    isDimensionsPending: false,
    isFeaturePending: false,
    error: null,
    table: initialPerformanceTableState
};

export function performanceReducer(state: IPerformanceReducerState, action: IPerformanceReducerAction): IPerformanceReducerState {
    switch (action.type) {
        case 'data/fetch':
            return {...state, isDataPending: true};
        case 'data/fetch/success':
            return {...state, isDataPending: false, data: action.data};
        case 'data/fetch/failed':
            return {...state, isDataPending: false, data: [], error: action.error};
        case 'dimensions/fetch':
            return {...state, isDimensionsPending: true};
        case 'dimensions/fetch/success':
            return {...state, isDimensionsPending: false, dimensions: action.dimensions};
        case 'dimensions/fetch/failed':
            return {...state, isDimensionsPending: false, dimensions: [], error: action.error};
        case 'query/performanceType':
            return {
                ...state,
                query: {...state.query, performanceType: action.performanceType, dimensions: []},
                data: []
            };
        case 'query/dateRange':
            const {dateRange} = action;
            return {...state, query: {...state.query, from: dateRange.startDate, to: dateRange.endDate}};
        case 'query/dimensions':
            return {...state, query: {...state.query, dimensions: action.dimensions}};
        case 'query/agencyId':
            return {...state, query: {...state.query, agencyId: action.agencyId}};
        case 'query/chartType':
            return {...state, query: {...state.query, chartType: action.chartType}};
        case 'query/granularity':
            return {...state, query: {...state.query, granularity: action.granularity}};
        case 'table/state':
            return {...state, table: action.table};
        case 'features/fetch':
            return {...state, isFeaturePending: true};
        case 'features/fetch/success':
            return {...state, isFeaturePending: false};
        case 'features/fetch/failed':
            return {...state, isFeaturePending: false, error: action.error};
        default:
            return state;
    }
}

export const usePerformanceReducer = (performanceQuery: IPerformanceQuery) => useReducer(performanceReducer, {
    ...initialPerformanceState,
    query: performanceQuery
});

const DashboardContext = React.createContext<{
    reducer: [IPerformanceReducerState, Dispatch<IPerformanceReducerAction>];
}>(null);

export const DashboardReducer = ({children, performanceQuery}: { performanceQuery: IPerformanceQuery; children: ReactNode }) =>
    <DashboardContext.Provider value={{
        reducer: usePerformanceReducer(performanceQuery),
    }} children={children}/>;

interface IDashboardReducer {
    state: IPerformanceReducerState;
    fetchData: (overrideQuery?: Partial<IPerformanceQuery>) => void;
    fetchDimensions: () => void;
    fetchFeatures: () => void;
    setGranularity: (granularity: Granularity) => void;
    setTableState: (tableState: IPerformanceTableState) => void;
    setPerformanceType: (performanceType: PerformanceType) => void;
    setDateRange: (dateRange: DateRange) => void;
    setDimensions: (dimensions: string[]) => void;
    setChartView: (chartView: ChartType) => void;
    setAgencyId: (agencyId: number) => void;
}

export const useDashboardReducer = (): IDashboardReducer => {
    let {reducer: [state, dispatch]} = useContext(DashboardContext);
    return {
        state,
        fetchData: (overrideQuery = {}) => {
            dispatch({type: 'data/fetch'});
            getPerformance({...state.query, ...overrideQuery})
                .then(data => dispatch({type: 'data/fetch/success', data}))
                .catch(error => dispatch({type: 'data/fetch/failed', error}));
        },
        fetchDimensions: () => {
            dispatch({type: 'dimensions/fetch'});
            getPerformanceDimensions(state.query)
                .then(data => dispatch({type: 'dimensions/fetch/success', dimensions: data}))
                .catch(error => dispatch({type: 'dimensions/fetch/failed', error}));
        },
        fetchFeatures: () => {
            dispatch({type: 'features/fetch'});
            fetchFeatures()
                .then(_ => dispatch({type: 'features/fetch/success'}))
                .catch(error => dispatch({type: 'features/fetch/failed', error}));
        },
        setGranularity: granularity => dispatch({type: 'query/granularity', granularity}),
        setTableState: tableState => dispatch({type: 'table/state', table: tableState}),
        setPerformanceType: performanceType => dispatch({type: 'query/performanceType', performanceType}),
        setDateRange: dateRange => dispatch({type: 'query/dateRange', dateRange}),
        setDimensions: dimensions => dispatch({type: 'query/dimensions', dimensions}),
        setChartView: chartView => dispatch({type: 'query/chartType', chartType: chartView}),
        setAgencyId: agencyId => dispatch({type: 'query/agencyId', agencyId})
    };
};
